/**
 * Education Hub — Progress Manager
 *
 * Handles localStorage read/write, REST API save/restore,
 * and Segment identify + track calls for Progress Saved and Progress Retrieved.
 *
 * Depends on: unlkEdu.restUrl and unlkEdu.nonce via wp_inline_script
 * Enqueue on education pages only.
 */

import Cookies from 'js-cookie';
import { getProgress } from '../utilities/_get-progress.js';
import { sendIdentify } from '../utilities/identify.js';


const STORAGE_KEY = 'edu_progress';
const EMAIL_KEY = 'edu_email';
const REST_BASE = globalThis.unlkEdu?.restUrl ?? '/wp-json/unlk/v1';
const NONCE = globalThis.unlkEdu?.nonce ?? '';
const RESTORE_DISMISS_COOKIE = 'edu_restore_dismissed';
document.addEventListener('DOMContentLoaded', init);


function init() {
  applyLessonStates();

  const restoreEl = document.getElementById('edu-restore-modal');

  if (restoreEl && shouldShowRestorePrompt()) {
    Cookies.set(RESTORE_DISMISS_COOKIE, '1', { expires: 7, path: '/', sameSite: 'Lax' });
    globalThis.unlk.Modal.getOrCreateInstance(restoreEl).show();
  }

  setHasVisitedCookie();

  // Restore form
  document.getElementById('edu-restore-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const emailInput = event.target.querySelector('[name="email"]');
    emailInput?.classList.remove('is-invalid');

    const email = emailInput?.value?.trim() ?? '';
    const result = await handleRestoreSubmit(email);

    if (result.success) {
      const restoreEl = document.getElementById('edu-restore-modal');
      if (restoreEl) globalThis.unlk.Modal.getOrCreateInstance(restoreEl).hide();
      globalThis.location.reload();
    } else if (emailInput) {
      emailInput.classList.add('is-invalid');
      const feedbackText = event.target.querySelector('.invalid-feedback span');
      if (feedbackText) feedbackText.textContent = result.error;
    }
  });

  // Dismiss buttons
  for (const button of document.querySelectorAll('[data-edu-dismiss-restore]')) {
    button.addEventListener('click', dismissRestorePrompt);
  }
}

function saveLocal(progress) {
  // eslint-disable-next-line camelcase
  const updated = { ...progress, updated_at: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  syncProgressCookie(updated);
  return updated;
}

function syncProgressCookie(progress) {
  const completed = (progress.completed_topics || []).join(',');
  const inProgress = (progress.in_progress_topics || []).join(',');
  Cookies.set('edu_completed', completed, { expires: 365, path: '/', sameSite: 'Lax' });
  Cookies.set('edu_in_progress', inProgress, { expires: 365, path: '/', sameSite: 'Lax' });
}

export function getSavedEmail() {
  return localStorage.getItem(EMAIL_KEY) || null;
}

function setSavedEmail(email) {
  localStorage.setItem(EMAIL_KEY, email);
}

/* ------------------------------------------------------------------
     MARK TOPIC COMPLETE
     Call from your "Continue" button handler.
     Returns { topicAdded, lessonCompleted } so the template
     knows whether to show the save prompt.
     ------------------------------------------------------------------ */

export function completeTopicAndSync(topicId, topicUrl, lessonTopicIds, lessonId) {
  const progress = getProgress();

  const topicAdded = !progress.completed_topics.includes(topicId);
  if (topicAdded) {
    progress.completed_topics.push(topicId);
  }

  // Remove from in-progress now that it's complete
  // eslint-disable-next-line camelcase
  progress.in_progress_topics = (progress.in_progress_topics || []).filter((id) => id !== topicId);

  // eslint-disable-next-line camelcase
  progress.last_topic_id = topicId;
  // eslint-disable-next-line camelcase
  progress.last_topic_url = topicUrl;

  const lessonCompleted =
    lessonTopicIds.length > 0 &&
    !progress.completed_lessons.includes(lessonId) &&
    lessonTopicIds.every((id) => progress.completed_topics.includes(id));

  if (lessonCompleted) {
    progress.completed_lessons.push(lessonId);
  }

  const saved = saveLocal(progress);

  // If user already gave email, silently keep DB in sync
  const email = getSavedEmail();
  if (email) {
    // Intentionally fire-and-forget — failure is non-critical
    // eslint-disable-next-line no-void
    void saveToServer(email, saved);
  }

  return { topicAdded, lessonCompleted };
}

/* ------------------------------------------------------------------
     REST API
     ------------------------------------------------------------------ */

async function saveToServer(email, progress) {
  const data = progress ?? getProgress();

  const response = await fetch(`${REST_BASE}/education/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': NONCE
    },
    body: JSON.stringify({ email, ...data })
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || 'Save failed');
  }

  return response.json();
}

async function restoreFromServer(email) {
  const response = await fetch(`${REST_BASE}/education/restore`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': NONCE
    },
    body: JSON.stringify({ email })
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || 'Restore failed');
  }

  const json = await response.json();
  saveLocal(json.progress);
  setSavedEmail(email);

  return json;
}

function dismissRestorePrompt() {
  const restoreEl = document.getElementById('edu-restore-modal');
  if (restoreEl) globalThis.unlk.Modal.getOrCreateInstance(restoreEl).hide();
}

/* ------------------------------------------------------------------
     RESTORE PROMPT
     ------------------------------------------------------------------ */

function shouldShowRestorePrompt() {
  if (getProgress().completed_topics.length > 0) return false;
  if (!Cookies.get('edu_has_visited')) return false;
  if (Cookies.get('SESSedusession')) return false;
  if (Cookies.get(RESTORE_DISMISS_COOKIE)) return false;

  return true;
}

function setHasVisitedCookie() {
  Cookies.set('edu_has_visited', '1', { expires: 30, path: '/', sameSite: 'Lax' });
  Cookies.set('SESSedusession', '1', { path: '/', sameSite: 'Lax' });
}

async function handleRestoreSubmit(email) {
  if (!isValidEmail(email)) {
    return { success: false, error: 'Please enter a valid email address.' };
  }

  try {
    const result = await restoreFromServer(email);

    if (!result) {
      const segmentId = Cookies.get('ajs_user_id') || Cookies.get('ajs_anonymous_id');
      if (segmentId) {
        sendIdentify(segmentId, { email });
      }

      return { success: false, error: 'No progress found for this email address.' };
    }

    const { progress } = result;
    // const lastActive = new Date(progress.updated_at);
    // const daysSince = Math.floor((Date.now() - lastActive) / 86_400_000);

    return {
      success: true,
      progress,
      resumeUrl: progress.last_topic_url
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

function isValidEmail(email) {
  return Boolean(email) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function resolveLessonStatus({ isDone, isInProgress }) {
  if (isDone) return 'complete';
  if (isInProgress) return 'in-progress';
  return 'unlocked';
}

function parseTopicUrls(button) {
  try {
    return JSON.parse(button.dataset.topicUrls ?? '[]');
  } catch {
    return [];
  }
}

function updateStartButton(button, { isDone, isInProgress, topicUrls, completedTopics }) {
  if (!button || topicUrls.length === 0) return;

  if (isDone) {
    button.hidden = true;
    return;
  }

  const nextTopic = topicUrls.find((t) => !completedTopics.has(Number(t.id)));
  if (!nextTopic) return;

  button.href = nextTopic.url;
  button.textContent = isInProgress ? 'Continue' : 'Get Started';
  button.hidden = false;
}

function hydrateLessonCard(card, lesson, { completedTopics, completedLessons }) {
  const topicIds = lesson.topic_ids ?? [];
  const isDone = completedLessons.has(Number(lesson.id));
  const topicsDone = topicIds.filter((id) => completedTopics.has(Number(id))).length;
  const isInProgress = !isDone && topicsDone > 0;

  const status = resolveLessonStatus({ isDone, isInProgress });
  card.dataset.status = status;

  const completeEl = card.querySelector('.edu-topics-complete');
  if (completeEl) completeEl.textContent = String(topicsDone);

  for (const pill of card.querySelectorAll('[data-topic-id]')) {
    const topicId = Number(pill.dataset.topicId);
    pill.classList.toggle('is-complete', completedTopics.has(topicId));
  }

  const button = card.querySelector('.edu-start-btn');
  updateStartButton(button, {
    isDone,
    isInProgress,
    topicUrls: button ? parseTopicUrls(button) : [],
    completedTopics
  });

  return status;
}

function readLessonFromCard(card) {
  let topicIds;
  try {
    topicIds = JSON.parse(card.dataset.topicIds ?? '[]');
  } catch {
    topicIds = [];
  }

  return {
    id: Number(card.dataset.lessonId),
    // eslint-disable-next-line camelcase
    topic_ids: topicIds.map(Number)
  };
}

function applyLessonStates() {
  const cards = [...document.querySelectorAll('.lesson-card[data-lesson-id]')];
  if (cards.length === 0) return;

  const progress = getProgress();
  const completedTopics = new Set((progress.completed_topics || []).map(Number));
  const completedLessons = new Set((progress.completed_lessons || []).map(Number));

  // Clear any server-rendered active marker; we'll re-place it.
  document.getElementById('active-lesson')?.removeAttribute('id');

  let activeLessonSet = false;
  for (const card of cards) {
    const lesson = readLessonFromCard(card);

    const status = hydrateLessonCard(card, lesson, {
      completedTopics,
      completedLessons
    });

    if (!activeLessonSet && (status === 'unlocked' || status === 'in-progress')) {
      card.id = 'active-lesson';
      activeLessonSet = true;
    }
  }
}

if (typeof globalThis !== 'undefined') {
  globalThis.unlk = globalThis.unlk || {};
  globalThis.unlk.completeTopicAndSync = completeTopicAndSync;
  globalThis.unlk.getSavedEmail = getSavedEmail;
  globalThis.unlk.setSavedEmail = setSavedEmail;
}

